import { ComponentChildren, h } from "preact";
import { useRef } from "preact/hooks";

import classname from "../../../../foundation/utils/classname";
import { FONT_FAMILY } from "../../../../foundation/utils/font";
import { getLineByWords, getLineWithLeadingEllipsis } from "../../../../foundation/utils/getTruncatedName";

import { MenuButtonLabelOverflowBehavior } from "./types";

import styles from "./MenuButtonLabel.module.css";

interface Props {
    children?: ComponentChildren;
    overflowBehavior?: MenuButtonLabelOverflowBehavior;
}

export default function MenuButtonLabel({
    children,
    overflowBehavior = MenuButtonLabelOverflowBehavior.Ellipsis
}: Props) {
    const ref = useRef<HTMLSpanElement>(null);

    function getContent() {
        const label = children;

        if (!ref.current || overflowBehavior === "ellipsis") {
            return label;
        }

        if (typeof label !== "string") {
            return label;
        }

        const font = `400 13px ${FONT_FAMILY}`;
        const lineWidth = ref.current.offsetWidth;

        const firstLine = getLineByWords(label, font, lineWidth);
        const rest = label.substring(firstLine.length).trimStart();
        const secondLine = getLineWithLeadingEllipsis(rest, font, lineWidth);

        return (
            <div class={styles.menuButtonLabel} title={label}>
                <span>{firstLine}</span>
                <span>{secondLine}</span>
            </div>
        );
    }

    // if children is jsx, its expected to handle its own overflow / title expectations
    const title = overflowBehavior === "ellipsis" && typeof children === "string" ? children : undefined;

    return (
        <span
            ref={ref}
            class={classname("content", {
                ellipsis: overflowBehavior === MenuButtonLabelOverflowBehavior.Ellipsis
            })}
            title={title}>
            {getContent()}
        </span>
    );
}
